import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { first } from 'rxjs';
import { CustomerProductPrice } from './../../../../../../models/price';
import { Product } from './../../../../../../models/product';
import { AuthService } from './../../../../../../services/auth/auth.service';
import { AvailabilityService } from './../../../../../../services/availability/availability.service';
import { CartListCheckoutConfigService } from './../../../../../../services/cart-list-checkout-config/cart-list-checkout-config.service';
import { CartService } from './../../../../../../services/cart/cart.service';
import { GungFlowService } from './../../../../../../services/gung-flow/gung-flow.service';
import { PriceService } from './../../../../../../services/price/price.service';
import { ProductInputQuantityConfigService } from './../../../../../../services/product-input-quantity-config/product-input-quantity-config.service';
import { ProductService } from './../../../../../../services/products/product.service';

@Component({
    selector: 'lib-checkout-add-row-modal',
    templateUrl: './checkout-add-row-modal.component.html',
    styleUrls: ['./checkout-add-row-modal.component.css'],
    standalone: false
})
export class CheckoutAddRowModalComponent implements OnInit {
  private orderLineItemProductId = '';
  disableNonAvailable = false;
  isSalesOrAdmin: boolean = false;
  isUser: boolean = false;
  isSales: boolean = false;
  isAdmin: boolean = false;
  isAnonymous: boolean = false;

  modalRows: {
    productId: string;
    qty: number;
    price: number;
    discount: number;
    productObject?: Product;
    priceObject?: CustomerProductPrice;
  }[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    protected cartService: CartService,
    protected productService: ProductService,
    protected priceService: PriceService,
    protected authService: AuthService,
    public cartListCheckoutConfig: CartListCheckoutConfigService,
    protected productInputQuantityConfigService: ProductInputQuantityConfigService,
    protected gungFlowService: GungFlowService,
    protected availabilityService: AvailabilityService,
    protected gungNotificationService: GungNotificationService,
    protected translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService.getRoles().pipe(first()).subscribe(roles => {
      this.isSalesOrAdmin = roles.indexOf('ADMIN') > -1 || roles.indexOf('SALES') > -1;
      this.isSales = roles.indexOf('SALES') > -1;
      this.isAdmin = roles.indexOf('ADMIN') > -1;
      this.isUser = roles.indexOf('USER') > -1 && !this.isAdmin && !this.isUser;
      this.isAnonymous = roles.indexOf('ANONYMOUS') > -1;
    });
    this.addNewRow();

    this.gungFlowService
      .getSelectedFlow()
      .pipe(first())
      .subscribe(flow => {
        this.disableNonAvailable = flow.disableNonAvailable;
      });
  }

  addNewRow() {
    this.modalRows.push({
      productId: this.orderLineItemProductId,
      qty: 1,
      price: undefined,
      discount: undefined
    });
  }

  removeRow(index) {
    this.modalRows.splice(index, 1);
  }

  selectProduct(product: Product, rowIndex: number, productElement) {
    if (this.disableNonAvailable) {
       this.availabilityService.getAvailability(product.id).pipe(first()).subscribe(av=> {
        if (av && av.maxFutureStock === 0) {
          productElement.inputTextElement.value = null;
          productElement.model = null;
          this.cdr.detectChanges();
          this.gungNotificationService.notify(
            this.translateService.instant('UNAVAILABLE_PRODUCT'),
            this.translateService.instant('UNAVAILABLE_PRODUCT'),
            'warning'
          );
        } else {
          this.modalRows[rowIndex].productId = product.id;
          this.modalRows[rowIndex].productObject = product;
          this.priceService
            .getCurrentCustomerPrice(product.id)
            .pipe(first())
            .subscribe(price => {
              this.modalRows[rowIndex].priceObject = price;
              this.validateQuantity(1, rowIndex, undefined)
            }
          );
        }
       })
    } else {
      this.modalRows[rowIndex].productId = product.id;
      this.modalRows[rowIndex].productObject = product;
      this.priceService
        .getCurrentCustomerPrice(product.id)
        .pipe(first())
        .subscribe(price => {
          this.modalRows[rowIndex].priceObject = price;
          this.validateQuantity(1, rowIndex, undefined)
        }
      );
    }
  }

  validateQuantity(value: number, rowIndex: number, target?): void {
    const {productId, productObject: product, priceObject: price} = this.modalRows[rowIndex];
    const allowManualQtyInputOutsideStepSizes = this.productInputQuantityConfigService.getAllowManualQtyInputOutsideStepSizes(productId, product, price, this.isAnonymous, this.isUser, this.isSales, this.isAdmin);
    const minimumOrderQuantity = this.productInputQuantityConfigService.getMinimumOrderQuantity(productId, product, price);
    const stepAmount = this.productInputQuantityConfigService.getStepAmount(productId, product, price);
    const recomendedInitOrderQuantity = this.productInputQuantityConfigService.getRecomendedInitOrderQuantity(productId, product, price);
    const showDecimals = this.productInputQuantityConfigService.showDecimals(productId, product);
    // const allowPurchaseOverAvailability = this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(value, availability, product, roles);

    /* if (!allowManualQtyInputOutsideStepSizes) {
      value = 0;
    } else if (allowManualQtyInputOutsideStepSizes) {
      //
    } */

    if (value < recomendedInitOrderQuantity) {
      value = recomendedInitOrderQuantity;
    } else if (value >= minimumOrderQuantity && (stepAmount === 1 || value % stepAmount === 0)) {
      //
    } else if (!allowManualQtyInputOutsideStepSizes && value >= 0 && Math.abs((value % stepAmount)) > Number.EPSILON) {
      value += stepAmount - (value % stepAmount);
    }

    if(showDecimals === 0) {
      value = Math.floor(value);
    } else {
      value = Number(Number(value).toFixed(showDecimals));
    }

    if (value <= 0) {
      this.modalRows[rowIndex].qty = recomendedInitOrderQuantity;
    }
    if (target) {
      target.value = value;
    } else {
      this.modalRows[rowIndex].qty = value;
    }
  }

  submitRows() {
    if (this.modalRows.length === 0) {
      this.activeModal.close();
      return;
    }
    const ids = [];
    for (const modalRow of this.modalRows) {
      if (modalRow.productId) {

        let extra = {}
        const price = this.cartListCheckoutConfig.changePriceField;
        const discount = this.cartListCheckoutConfig.changeDiscountField
        if(this.cartListCheckoutConfig.enableSalesEditPrice){
          this.setObjectPropertyValue(extra, price, modalRow.price);
        }
        if(this.cartListCheckoutConfig.enableSalesEditDiscount){
          this.setObjectPropertyValue(extra, discount, modalRow.discount);
        }
        this.cartService.addToCart(modalRow.productId, modalRow.qty, undefined, undefined, undefined, extra);
        ids.push(modalRow.productId);
      }
    }

    this.activeModal.close({ ids });
  }

  productSearchFormatter(result: Product) {
    return result.id + ' - ' + result.name;
  }

  productSearchInputFormatter(result: Product) {
    return result.id;
  }

  focusNextInput(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const row = target.closest('tr');
    if (!row) {
      return;
    }
    const inputs = Array.from(row.querySelectorAll<HTMLElement>('input'));
    const currentIndex = inputs.indexOf(target as HTMLInputElement);
    if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
      event.preventDefault();
      (inputs[currentIndex + 1] as HTMLInputElement).focus();
      (inputs[currentIndex + 1] as HTMLInputElement).select();
    } else if (currentIndex === inputs.length - 1) {
      event.preventDefault();
      this.addNewRow();
      this.cdr.detectChanges();
      const nextRow = row.nextElementSibling as HTMLElement;
      if (nextRow) {
        const nextRowInputs = Array.from(nextRow.querySelectorAll<HTMLInputElement>('input'));
        if (nextRowInputs.length > 0) {
          nextRowInputs[0].focus();
          nextRowInputs[0].select();
        }
      }
    }
  }

  setObjectPropertyValue(obj, propertyString, value) {
    const properties = propertyString.split('.');
    const lastPropertyIndex = properties.length - 1;
    properties.reduce((result, property, index) => {
      if (index === lastPropertyIndex) {
        result[property] = value; // Set the value of the final property
      } else if (property in result) {
        return result[property]; // Access the property
      } else {
        result[property] = {}; // Create nested objects if the property doesn't exist
        return result[property];
      }
    }, obj);
  }
}
